// core/services/activity-logs.service.ts
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CookieService } from "ngx-cookie-service";
import { Observable } from "rxjs";
import { ActivityLog } from "src/app/pages/activity-logs/activity-logs.component";
import { environment } from "src/environments/environment";

@Injectable({ providedIn: "root" })
export class ActivityLogsService {
  constructor(private http: HttpClient, private cookieService: CookieService) {}

  getActivityLogs(params: {
    page: number;
    pageSize: number;
    action?: string;
    status?: "success" | "error";
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    entityType?:
      | "property"
      | "login"
      | "deal"
      | "contact"
      | "logout"
      | "sale"
      | "ai"
      | "user";
  }): Observable<{
    items: ActivityLog[];
    total: number;
    hasMore?: boolean;
    totalIsEstimated?: boolean;
  }> {
    let httpParams = new HttpParams()
      .set("page", String(params.page))
      .set("pageSize", String(params.pageSize));

    const lang = this.cookieService.get("lang") || "en";
    httpParams = httpParams.set("lang", lang);

    if (params.action !== undefined && params.action !== null) {
      httpParams = httpParams.set("action", String(params.action));
    }

    if (params.status) {
      httpParams = httpParams.set("status", params.status);
    }

    if (params.dateFrom) {
      httpParams = httpParams.set("dateFrom", params.dateFrom);
    }

    if (params.dateTo) {
      httpParams = httpParams.set("dateTo", params.dateTo);
    }

    if (params.search) {
      httpParams = httpParams.set("search", params.search);
    }

    if (params.entityType) {
      httpParams = httpParams.set("entityType", params.entityType);
    }

    return this.http.get<{
      items: ActivityLog[];
      total: number;
      hasMore?: boolean;
      totalIsEstimated?: boolean;
    }>(
      `${environment.apiUrl}/activity-logs`,
      { params: httpParams, withCredentials: true }
    );
  }

  getActivityActions(): Observable<{ value: string; label: string }[]> {
    return this.http.get<{ value: string; label: string }[]>(
      `${environment.apiUrl}/activity-logs/actions`,
      { withCredentials: true }
    );
  }

  /** Histórico de um imóvel (também de imóveis já eliminados). */
  getPropertyHistory(
    propertyId: string,
    params: PropertyHistoryParams
  ): Observable<PropertyHistoryResponse> {
    return this.http.get<PropertyHistoryResponse>(
      `${environment.apiUrl}/activity-logs/properties/${propertyId}`,
      { params: this.buildHistoryParams(params), withCredentials: true }
    );
  }

  exportPropertyHistory(
    propertyId: string,
    params: PropertyHistoryParams
  ): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}/activity-logs/properties/${propertyId}/export`,
      {
        params: this.buildHistoryParams(params),
        withCredentials: true,
        responseType: "blob",
      }
    );
  }

  /**
   * Regista um evento do imóvel visto do lado do cliente (abertura da ficha,
   * imagem vista/descarregada). Nunca interrompe o fluxo de quem chama.
   */
  trackPropertyEvent(
    propertyId: string,
    body: {
      action:
        | "PROPERTY_VIEW"
        | "PROPERTY_IMAGE_VIEWED"
        | "PROPERTY_IMAGE_DOWNLOAD_REQUESTED";
      fileId?: number;
      navigationId?: string;
      context?: string;
    }
  ): void {
    if (!propertyId) return;
    this.http
      .post(
        `${environment.apiUrl}/activity-logs/properties/${propertyId}/events`,
        body,
        { withCredentials: true }
      )
      .subscribe({ error: () => undefined });
  }

  /** Id de uma navegação (uma abertura da ficha). */
  newNavigationId(): string {
    const cryptoRef: any = (window as any).crypto;
    if (cryptoRef?.randomUUID) return cryptoRef.randomUUID();
    return `${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 12)}`;
  }

  private buildHistoryParams(params: PropertyHistoryParams): HttpParams {
    let httpParams = new HttpParams()
      .set("page", String(params.page ?? 1))
      .set("pageSize", String(params.pageSize ?? 10))
      .set("lang", this.cookieService.get("lang") || "en");

    const optional: Array<[string, string | undefined | null]> = [
      ["dateFrom", params.dateFrom],
      ["dateTo", params.dateTo],
      ["actions", params.actions?.length ? params.actions.join(",") : null],
      ["userId", params.userId],
      ["source", params.source],
      ["status", params.status],
      ["search", params.search?.trim()],
    ];
    optional.forEach(([key, value]) => {
      if (value) httpParams = httpParams.set(key, value);
    });

    return httpParams;
  }
}

export interface PropertyHistoryParams {
  page?: number;
  pageSize?: number;
  dateFrom?: string | null;
  dateTo?: string | null;
  actions?: string[] | null;
  userId?: string | null;
  source?: string | null;
  status?: "success" | "error" | null;
  search?: string | null;
}

export interface PropertyHistoryLog {
  id: number;
  action: string;
  description?: string | null;
  status: boolean;
  source?: string | null;
  correlationId?: string | null;
  propertyId?: string | null;
  auditPropertyId?: string | null;
  createdBy?: string | null;
  createdAt: string;
  metadata?: any;
  user?: { id: string; name?: string | null; email?: string | null } | null;
  // MASTER que fez a ação em "Ver como" (a ação fica em nome de `user`).
  impersonator?: { id: string; name?: string | null; email?: string | null } | null;
}

export interface PropertyHistoryResponse {
  items: PropertyHistoryLog[];
  total: number;
  page: number;
  pageSize: number;
  actors?: Array<{ id: string; name: string; email?: string | null }>;
}
